// src/features/groups/hooks/useGroupPermissions.ts

import { useMemo } from 'react';
import { useResourcePermissions } from '@/hooks/useAppPermissions';
import { Group } from '../types';

/**
 * Resultado completo del hook de permisos de grupos
 */
export interface UseGroupPermissionsResult {
  // Permisos básicos
  canManageGroups: boolean;
  canViewGroups: boolean;
  canCreateGroups: boolean;
  canEditGroups: boolean;
  canDeleteGroups: boolean;
  
  // Permisos extendidos
  canManageHotels: boolean;
  canViewStats: boolean;
  canExportData: boolean;
  canEditThisGroup: boolean;
  canDeleteThisGroup: boolean;
  
  // Permisos de negocio específicos
  canAddHotelsToGroup: (group?: Group) => boolean;
  canRemoveHotelsFromGroup: (group?: Group) => boolean;
  canTransferHotelsBetweenGroups: () => boolean;
  canViewGroupFinancials: (group?: Group) => boolean;
  canMergeGroups: () => boolean;
  canArchiveGroup: (group?: Group) => boolean;
  
  // Utilidades del sistema base
  hasRole: (...roles: string[]) => boolean;
  isAtLeastRole: (minRole: string) => boolean;
  currentRole: string | null;
}

/**
 * Hook específico para permisos del feature de Grupos
 * Extiende los permisos base con lógica específica de grupos
 */
export const useGroupPermissions = (group?: Group): UseGroupPermissionsResult => {
  // Usar el hook base de permisos para el recurso 'groups'
  const permissions = useResourcePermissions('groups', group);
  
  // Crear el objeto de permisos específicos para grupos
  const groupPermissions = useMemo<UseGroupPermissionsResult>(() => {
    // ========================================
    // PERMISOS BÁSICOS
    // ========================================
    const canViewGroups = permissions.canView;
    const canManageGroups = permissions.canModify && permissions.canAdd && permissions.canRemove;
    const canCreateGroups = permissions.canAdd;
    const canEditGroups = permissions.canModify;
    const canDeleteGroups = permissions.canRemove;
    
    // ========================================
    // PERMISOS EXTENDIDOS
    // ========================================
    const canManageHotels = permissions.canPerform('groups', 'manageHotels' as any);
    const canViewStats = permissions.canPerform('groups', 'viewStats' as any);
    const canExportData = permissions.canExport('groups');
    
    // Permisos condicionales cuando se proporciona un grupo específico
    const canEditThisGroup = group ? permissions.canEditThis(group) : canEditGroups;
    const canDeleteThisGroup = group ? permissions.canDeleteThis(group) : canDeleteGroups;
    
    // ========================================
    // FUNCIONES DE PERMISOS DE NEGOCIO
    // ========================================
    
    /**
     * Verifica si el usuario puede agregar hoteles a un grupo
     */
    const canAddHotelsToGroup = (groupToCheck?: Group): boolean => {
      const targetGroup = groupToCheck || group;
      
      // SuperAdmin puede agregar hoteles a cualquier grupo
      if (permissions.hasRole('SuperAdmin')) return true;
      
      // GroupAdmin solo puede agregar hoteles a su propio grupo
      if (permissions.hasRole('GroupAdmin')) {
        const currentUserGroupId = localStorage.getItem('groupId');
        return currentUserGroupId === targetGroup?.id;
      }
      
      return false;
    };
    
    /**
     * Verifica si el usuario puede remover hoteles de un grupo
     * (Misma lógica que agregar)
     */
    const canRemoveHotelsFromGroup = (groupToCheck?: Group): boolean => {
      return canAddHotelsToGroup(groupToCheck);
    };
    
    /**
     * Verifica si el usuario puede transferir hoteles entre grupos
     */
    const canTransferHotelsBetweenGroups = (): boolean => {
      // Solo SuperAdmin puede transferir hoteles entre grupos
      return permissions.hasRole('SuperAdmin');
    };
    
    /**
     * Verifica si el usuario puede ver información financiera del grupo
     */
    const canViewGroupFinancials = (groupToCheck?: Group): boolean => {
      const targetGroup = groupToCheck || group;
      
      // SuperAdmin ve todo
      if (permissions.hasRole('SuperAdmin')) return true;
      
      // GroupAdmin solo ve financieros de su grupo
      if (permissions.hasRole('GroupAdmin')) {
        const currentUserGroupId = localStorage.getItem('groupId');
        return currentUserGroupId === targetGroup?.id;
      }
      
      return false;
    };
    
    /**
     * Verifica si el usuario puede fusionar grupos
     */
    const canMergeGroups = (): boolean => {
      // Solo SuperAdmin puede fusionar grupos
      return permissions.hasRole('SuperAdmin');
    };
    
    /**
     * Verifica si el usuario puede archivar un grupo
     */
    const canArchiveGroup = (groupToCheck?: Group): boolean => {
      const targetGroup = groupToCheck || group;
      
      // No se puede archivar un grupo con hoteles activos
      if (targetGroup && targetGroup.hotelCount && targetGroup.hotelCount > 0) {
        return false;
      }
      
      // Solo SuperAdmin puede archivar grupos
      return permissions.hasRole('SuperAdmin');
    };
    
    // ========================================
    // RETORNAR OBJETO COMPLETO
    // ========================================
    return {
      // Permisos básicos
      canManageGroups,
      canViewGroups,
      canCreateGroups,
      canEditGroups,
      canDeleteGroups,
      
      // Permisos extendidos
      canManageHotels,
      canViewStats,
      canExportData,
      canEditThisGroup,
      canDeleteThisGroup,
      
      // Funciones de permisos de negocio
      canAddHotelsToGroup,
      canRemoveHotelsFromGroup,
      canTransferHotelsBetweenGroups,
      canViewGroupFinancials,
      canMergeGroups,
      canArchiveGroup,
      
      // Utilidades del sistema base
      hasRole: permissions.hasRole,
      isAtLeastRole: permissions.isAtLeastRole,
      currentRole: permissions.role,
    };
  }, [permissions, group]);
  
  return groupPermissions;
};

/**
 * Hook para verificar permisos sobre múltiples grupos
 * Útil cuando necesitas verificar permisos en una lista
 */
export const useGroupsListPermissions = (groups: Group[] = []) => {
  const basePermissions = useGroupPermissions();
  
  return useMemo(() => {
    const groupPermissionsMap = new Map<string, UseGroupPermissionsResult>();
    
    // Pre-calcular permisos para cada grupo
    groups.forEach(group => {
      // Crear un objeto de permisos específico para cada grupo
      const groupSpecificPerms: UseGroupPermissionsResult = {
        ...basePermissions,
        // Sobreescribir permisos específicos que dependen del grupo
        canEditThisGroup: basePermissions.hasRole('SuperAdmin') || 
          (basePermissions.hasRole('GroupAdmin') && 
           localStorage.getItem('groupId') === group.id),
        canDeleteThisGroup: basePermissions.canDeleteGroups && 
          (!group.hotelCount || group.hotelCount === 0),
        canArchiveGroup: () => basePermissions.canArchiveGroup(group),
        canViewGroupFinancials: () => basePermissions.canViewGroupFinancials(group),
        canAddHotelsToGroup: () => basePermissions.canAddHotelsToGroup(group),
        canRemoveHotelsFromGroup: () => basePermissions.canRemoveHotelsFromGroup(group),
      };
      
      groupPermissionsMap.set(group.id, groupSpecificPerms);
    });
    
    return {
      /**
       * Obtiene permisos específicos para un grupo
       */
      getPermissionsForGroup: (groupId: string): UseGroupPermissionsResult => 
        groupPermissionsMap.get(groupId) || basePermissions,
      
      /**
       * Verifica si puede editar algún grupo de la lista
       */
      canEditAny: groups.some(g => 
        groupPermissionsMap.get(g.id)?.canEditThisGroup || false
      ),
      
      /**
       * Verifica si puede eliminar algún grupo de la lista
       */
      canDeleteAny: groups.some(g => 
        groupPermissionsMap.get(g.id)?.canDeleteThisGroup || false
      ),
      
      /**
       * Obtiene los IDs de grupos que puede editar
       */
      editableGroupIds: groups
        .filter(g => groupPermissionsMap.get(g.id)?.canEditThisGroup)
        .map(g => g.id),
      
      /**
       * Obtiene los IDs de grupos que puede eliminar
       */
      deletableGroupIds: groups
        .filter(g => groupPermissionsMap.get(g.id)?.canDeleteThisGroup)
        .map(g => g.id),
    };
  }, [groups, basePermissions]);
};

// Exportación por defecto
export default useGroupPermissions;